/**
 * Card sorting utilities.
 */
export const RANK_ORDER = ["A", "2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K", "JOKER"];
// Used when Ace is high (Q-K-A context): place A after K.
export const RANK_ORDER_HIGH_ACE = ["2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K", "A", "JOKER"];
const SUIT_ORDER = ["spades", "hearts", "diamonds", "clubs"];

const isRed = (c) => c.suit === "hearts" || c.suit === "diamonds";
const bySuitOrder = (arr) =>
  [...arr].sort((a, b) => SUIT_ORDER.indexOf(a.suit || "zzz") - SUIT_ORDER.indexOf(b.suit || "zzz"));

// Sort a same-rank group using the brelan color rule.
const sortSameRankGroup = (group) => {
  const reds = group.filter(isRed);
  const blacks = group.filter((c) => !isRed(c));
  if (group.length === 3) {
    if (reds.length === 2) return [...bySuitOrder(reds), ...bySuitOrder(blacks)];
    if (blacks.length === 2) return [...bySuitOrder(blacks), ...bySuitOrder(reds)];
  }
  if (group.length === 4) {
    return [...bySuitOrder(blacks), ...bySuitOrder(reds)];
  }
  return bySuitOrder(group);
};

export const sortByRank = (cards) => {
  return [...cards].sort((a, b) => {
    const ra = RANK_ORDER.indexOf(a.rank);
    const rb = RANK_ORDER.indexOf(b.rank);
    if (ra !== rb) return ra - rb;
    const sa = SUIT_ORDER.indexOf(a.suit || "zzz");
    const sb = SUIT_ORDER.indexOf(b.suit || "zzz");
    return sa - sb;
  });
};

/**
 * Sort by ascending rank value, applying the brelan color rule within same-rank groups:
 * - 3 cards of same rank: same-color pair (rouge/noir) on left, odd color on right
 * - 4 cards of same rank: sorted ♠♥♦♣
 * - other counts: sorted ♠♥♦♣
 */
export const sortByValue = (cards) => {
  const groups = {};
  for (const c of cards) {
    if (!groups[c.rank]) groups[c.rank] = [];
    groups[c.rank].push(c);
  }
  return RANK_ORDER
    .filter((r) => groups[r])
    .flatMap((r) => sortSameRankGroup(groups[r]));
};

export const sortBySuit = (cards) => {
  return [...cards].sort((a, b) => {
    const sa = SUIT_ORDER.indexOf(a.suit || "zzz");
    const sb = SUIT_ORDER.indexOf(b.suit || "zzz");
    if (sa !== sb) return sa - sb;
    const ra = RANK_ORDER.indexOf(a.rank);
    const rb = RANK_ORDER.indexOf(b.rank);
    return ra - rb;
  });
};

// The two orders a sequence can follow: Ace low (A-2-3) or Ace high (Q-K-A).
const RUN_ORDERS = [RANK_ORDER.slice(0, 13), RANK_ORDER_HIGH_ACE.slice(0, 13)];

// Every way the cards can lie as a sequence, Ace low first, then by the number
// of Jokers before the lowest real card: { order, start, left, extra }, where
// `start` is the index in `order` of the first card and `extra` the Jokers left
// over once the gaps are filled. Two Jokers never sit side by side (the server's
// `jokers_apart`): at most one Joker between two real cards, one past each end.
const runPlacements = (cards) => {
  const nonJokers = cards.filter((c) => !c.is_joker);
  const out = [];
  if (nonJokers.length === 0) return out;
  for (const order of RUN_ORDERS) {
    const pos = nonJokers.map((c) => order.indexOf(c.rank)).sort((a, b) => a - b);
    if (pos[0] < 0 || pos.some((p, i) => i > 0 && p === pos[i - 1])) continue;
    if (pos.some((p, i) => i > 0 && p - pos[i - 1] > 2)) continue;
    const extra = cards.length - (pos[pos.length - 1] - pos[0] + 1);
    for (let left = 0; left <= extra; left++) {
      const start = pos[0] - left;
      if (left > 1 || extra - left > 1) continue;
      if (start >= 0 && start + cards.length <= order.length) out.push({ order, start, left, extra });
    }
  }
  return out;
};

/**
 * Lay out a sequence the way the server stores it (`canonicalize_sequence` in
 * backend/game/combinations.py): ascending, Jokers in the gaps, the extra ones
 * at the ends, never two side by side. The number of Jokers the cards put in
 * front is kept when that end has room; otherwise each goes where it fits —
 * 10-J-Q-K-A plus a Joker can only be 9-10-J-Q-K-A, so that Joker goes on the
 * left, and 5-6-7 plus two Jokers is J-5-6-7-J.
 * Returns null when the cards make no sequence (5-J-J-8: the Jokers would touch).
 */
export const layoutSequence = (cards) => {
  const placements = runPlacements(cards);
  if (placements.length === 0) return null;
  const lead = cards.findIndex((c) => !c.is_joker);
  const { order, start } =
    placements.find((p) => p.left === Math.min(lead, p.extra)) || placements[0];
  const byPos = new Map(cards.filter((c) => !c.is_joker).map((c) => [order.indexOf(c.rank), c]));
  const jokers = cards.filter((c) => c.is_joker);
  return cards.map((_, i) => byPos.get(start + i) || jokers.shift());
};

/**
 * Where one more Joker can go on a laid-out sequence: { left, right }, the rank
 * it would stand for at each end, or null when that end is closed — nothing
 * comes before the Ace of A-2-3, nor after the Ace of Q-K-A, and an end that
 * already ends with a Joker takes no other one (two Jokers never side by side).
 */
export const sequenceJokerEnds = (cards) => {
  const ends = { left: null, right: null };
  const run = layoutSequence(cards);
  if (!run) return ends;
  const first = run.findIndex((c) => !c.is_joker);
  for (const order of RUN_ORDERS) {
    const start = order.indexOf(run[first].rank) - first;
    const fits = start >= 0 && start + run.length <= order.length &&
      run.every((c, i) => c.is_joker || order.indexOf(c.rank) === start + i);
    if (!fits) continue;
    if (start > 0 && !run[0].is_joker) ends.left = ends.left || order[start - 1];
    if (start + run.length < order.length && !run[run.length - 1].is_joker) {
      ends.right = ends.right || order[start + run.length];
    }
  }
  return ends;
};

/**
 * Sort cards within a pending group for display:
 * - sequence: laid out by `layoutSequence` (Jokers in the gaps, then at an end)
 * - set (3 real cards): same-color pair on left, odd color on right
 * - set (other): real cards by suit, jokers at end
 */
export const sortGroupCards = (kind, cards) => {
  if (kind === "sequence") {
    const run = layoutSequence(cards);
    if (run) return run;
    // Not a sequence (should not happen for a validated group): ascending, Jokers last.
    return [...sortByRank(cards.filter((c) => !c.is_joker)), ...cards.filter((c) => c.is_joker)];
  }

  // set — jokers always last; real cards use color-grouping when ≥ 3
  const nonJokers = cards.filter((c) => !c.is_joker);
  const jokers    = cards.filter((c) =>  c.is_joker);
  const sorted    = nonJokers.length >= 3 ? sortSameRankGroup(nonJokers) : bySuitOrder(nonJokers);
  return [...sorted, ...jokers];
};
